/**
 * ticket-filters.ts — how the Tickets list narrows the backlog.
 *
 * Status, type, priority, submitter, assignee, category, and the search box all filter client-side:
 * the page loads the whole backlog once (see `roadmapApi.listRequests`), so
 * changing a dropdown is instant and search spans every status rather than only
 * the visible one. Filters live in the URL so a view can be shared.
 */

import type {
  FeatureRequest,
  FeatureRequestPriority,
  FeatureRequestStatus,
  FeatureRequestType,
} from '@/api/endpoints/roadmap';
import {
  FEATURE_REQUEST_PRIORITIES,
  FEATURE_REQUEST_STATUSES,
  FEATURE_REQUEST_TYPES,
  formatFrId,
  TICKET_ORIGIN_LABELS,
} from '@/api/endpoints/roadmap';
import { matchesTicketAssignees } from './ticket-assignee';
import { ticketOriginLabel, ticketSubmitterKey, ticketSubmitterText } from './ticket-submitter';

/** A type dropdown value: a real kind, or the unclassified rows. */
export type TicketTypeFilterValue = FeatureRequestType | 'untyped';

/** A priority dropdown value: a real rank, or the unranked rows. */
export type TicketPriorityFilterValue = FeatureRequestPriority | 'unprioritized';

/** Wire stand-in for an empty category — a blank CSV cell would disappear. */
export const UNCATEGORIZED_CATEGORY_PARAM = '__none__';

/** "Intake" is everything awaiting a promote/decline decision. */
export const INTAKE_STATUSES: readonly FeatureRequestStatus[] = ['new', 'triaged'];

/**
 * Statuses that the default view leaves out. Finished and declined tickets are
 * history — keeping them out of the landing list is what stops a completed
 * ticket with a high score from sitting at the top of triage.
 */
export const CLOSED_STATUSES: ReadonlySet<FeatureRequestStatus> = new Set([
  'complete',
  'declined',
  'duplicate',
]);

/** Everything still in flight — the Status dropdown's landing selection. */
export const OPEN_STATUSES: readonly FeatureRequestStatus[] = FEATURE_REQUEST_STATUSES.filter(
  (status) => !CLOSED_STATUSES.has(status),
);

/** The URL / dropdown state that drives {@link filterTickets}. */
export type TicketListQuery = {
  statuses: FeatureRequestStatus[] | undefined;
  types: TicketTypeFilterValue[] | undefined;
  priorities: TicketPriorityFilterValue[] | undefined;
  submitters: string[] | undefined;
  assignees: string[] | undefined;
  categories: string[] | undefined;
  search: string;
  /**
   * Backlog view: hide tickets already committed to a live sprint or a
   * milestone, so the list is the unassigned work. Off = every ticket.
   */
  backlogOnly: boolean;
};

/** Bare `/admin/roadmap/requests` — open backlog tickets, no other narrowing. */
export const DEFAULT_TICKET_LIST_QUERY: TicketListQuery = {
  statuses: [...OPEN_STATUSES],
  types: undefined,
  priorities: undefined,
  submitters: undefined,
  assignees: undefined,
  categories: undefined,
  search: '',
  backlogOnly: true,
};

/** URL token for the non-default "every ticket" view (`?view=all`). */
const VIEW_ALL_PARAM = 'all';

/**
 * What `filterTickets` needs that is not on the ticket row: which tickets are
 * committed to a live sprint (from the sprints list, unresolved items only).
 */
export type TicketListContext = {
  sprintRequestIds: ReadonlySet<number>;
};

const EMPTY_CONTEXT: TicketListContext = { sprintRequestIds: new Set() };

/** True when the ticket is on a milestone or committed to a live sprint. */
export function isTicketAssigned(
  request: Pick<FeatureRequest, 'id' | 'milestoneId'>,
  context: TicketListContext,
): boolean {
  return request.milestoneId != null || context.sprintRequestIds.has(request.id);
}

/** Backlog view keeps unassigned tickets only; "all" keeps everything. */
export function matchesTicketBacklog(
  request: Pick<FeatureRequest, 'id' | 'milestoneId'>,
  backlogOnly: boolean,
  context: TicketListContext,
): boolean {
  return !backlogOnly || !isTicketAssigned(request, context);
}

const KNOWN_STATUS = new Set<string>(FEATURE_REQUEST_STATUSES);
const KNOWN_TYPE = new Set<string>([...FEATURE_REQUEST_TYPES, 'untyped']);
const KNOWN_PRIORITY = new Set<string>([...FEATURE_REQUEST_PRIORITIES, 'unprioritized']);

/** Display labels for the unified ticket/board status vocabulary. */
export const TICKET_STATUS_LABELS: Record<FeatureRequestStatus, string> = {
  new: 'New',
  triaged: 'Triaged',
  planning: 'Planning',
  development: 'In Development',
  qa: 'QA',
  acceptance_testing: 'Acceptance Testing',
  ready_for_release: 'Ready for Release',
  complete: 'Complete',
  blocked: 'Blocked',
  waiting_on_submitter: 'Waiting on Submitter',
  declined: 'Declined',
  duplicate: 'Duplicate',
};

/**
 * Each status names its environment and its owner (R5.16) — shown on hover
 * because "QA" means nothing without knowing where and by whom.
 */
export const TICKET_STATUS_DEFINITIONS: Record<FeatureRequestStatus, string> = {
  new: 'Just submitted — nobody has triaged it yet.',
  triaged: 'Reviewed by the team; type, category, and impact confirmed.',
  planning: 'Being scoped and scheduled — owner: the PM.',
  development: 'Being built — owner: the assigned engineer.',
  qa: 'Stage — development complete and deployed to stage. Owner: the QA resource.',
  acceptance_testing: 'Stage — QA passed. Owner: the submitter, who confirms the fix.',
  ready_for_release: 'Approved and queued for the next production release.',
  complete: 'Production — released and verified in prod.',
  blocked: 'The team cannot move it forward — the reason says why.',
  waiting_on_submitter: 'The team needs something from the person who raised it.',
  declined: 'Product or leadership decided not to proceed; the reason is recorded.',
  duplicate: 'Closed into another ticket — votes and watchers carry to the survivor.',
};

/** Human label for a ticket status (`QA`, not `Qa`). */
export function ticketStatusLabel(status: FeatureRequestStatus): string {
  return TICKET_STATUS_LABELS[status];
}

/** True when every value in `expected` is present and the lengths match. */
function sameValues<T extends string>(
  actual: readonly T[] | undefined,
  expected: readonly T[],
): boolean {
  if (!actual || actual.length !== expected.length) return false;
  const set = new Set(actual);
  return expected.every((value) => set.has(value));
}

/**
 * True when the URL state is the landing view: open statuses, no type /
 * priority / submitter / assignee / category / search. The count line uses this
 * to say "open of" instead of pretending the backlog is smaller than it is.
 */
export function isDefaultTicketListQuery(query: TicketListQuery): boolean {
  return (
    sameValues(query.statuses, OPEN_STATUSES) &&
    !query.types?.length &&
    !query.priorities?.length &&
    !query.submitters?.length &&
    !query.assignees?.length &&
    !query.categories?.length &&
    query.search.trim() === '' &&
    query.backlogOnly
  );
}

/** True when the current status filter includes completed tickets. */
export function ticketListIncludesComplete(query: TicketListQuery): boolean {
  return query.statuses === undefined || query.statuses.includes('complete');
}

/**
 * Adds or removes `complete` without touching the rest of the status set.
 * Hiding complete when only that status was selected falls back to open.
 */
export function setTicketListCompleteVisible(
  query: TicketListQuery,
  show: boolean,
): Partial<TicketListQuery> {
  if (show === ticketListIncludesComplete(query)) return {};
  const current: FeatureRequestStatus[] = query.statuses ?? [...FEATURE_REQUEST_STATUSES];
  if (show) {
    const next: FeatureRequestStatus[] = current.includes('complete')
      ? current
      : [...current, 'complete'];
    return {
      statuses: sameValues(next, FEATURE_REQUEST_STATUSES) ? undefined : next,
    };
  }
  const next = current.filter((status) => status !== 'complete');
  return { statuses: next.length === 0 ? [...OPEN_STATUSES] : next };
}

/** True when the ticket's status is in the selected set. Empty = every status. */
export function matchesTicketStatuses(
  request: Pick<FeatureRequest, 'status'>,
  statuses: readonly FeatureRequestStatus[] | undefined,
): boolean {
  if (!statuses || statuses.length === 0) return true;
  return statuses.includes(request.status);
}

/** True when the ticket's kind is in the selected set. Empty = every kind. */
export function matchesTicketTypes(
  request: Pick<FeatureRequest, 'ticketType'>,
  types: readonly TicketTypeFilterValue[] | undefined,
): boolean {
  if (!types || types.length === 0) return true;
  const kind: TicketTypeFilterValue = request.ticketType === '' ? 'untyped' : request.ticketType;
  return types.includes(kind);
}

/** Display label for a priority filter value (`Now`, not `now`). */
export function ticketPriorityLabel(priority: TicketPriorityFilterValue): string {
  if (priority === 'unprioritized') return 'Unprioritized';
  return priority.charAt(0).toUpperCase() + priority.slice(1);
}

/** True when the ticket's priority is in the selected set. Empty = every rank. */
export function matchesTicketPriorities(
  request: Pick<FeatureRequest, 'priority'>,
  priorities: readonly TicketPriorityFilterValue[] | undefined,
): boolean {
  if (!priorities || priorities.length === 0) return true;
  const rank: TicketPriorityFilterValue =
    request.priority.trim() === '' ? 'unprioritized' : (request.priority as FeatureRequestPriority);
  return priorities.includes(rank);
}

/** True when the ticket's submitter key is in the selected set. Empty = anyone. */
export function matchesTicketSubmitters(
  request: Pick<
    FeatureRequest,
    'createdByUserId' | 'createdByName' | 'createdByEmail' | 'requestedBy' | 'createdVia'
  >,
  submitters: readonly string[] | undefined,
): boolean {
  if (!submitters || submitters.length === 0) return true;
  return submitters.includes(ticketSubmitterKey(request));
}

/** True when the ticket's category is in the selected set. Empty = every category. */
export function matchesTicketCategories(
  request: Pick<FeatureRequest, 'category'>,
  categories: readonly string[] | undefined,
): boolean {
  if (!categories || categories.length === 0) return true;
  return categories.includes(request.category.trim());
}

/**
 * Unique category values on the loaded backlog, for the Category dropdown.
 * Blank categories become "Uncategorized" and sort last.
 */
export function collectCategoryOptions(
  tickets: readonly Pick<FeatureRequest, 'category'>[],
): Array<{ value: string; label: string }> {
  const values = new Set<string>();
  for (const ticket of tickets) {
    values.add(ticket.category.trim());
  }
  return [...values]
    .map((value) => ({ value, label: value || 'Uncategorized' }))
    .sort((a, b) => {
      if (a.value === '') return 1;
      if (b.value === '') return -1;
      return a.label.localeCompare(b.label);
    });
}

/**
 * Every field a search term may match. Kept in one place so the searchable
 * surface and the visible columns can be reasoned about together.
 */
function searchHaystack(request: FeatureRequest): string {
  return [
    formatFrId(request.frNumber),
    String(request.frNumber),
    request.name,
    request.description,
    request.ticketType,
    request.origin,
    TICKET_ORIGIN_LABELS[request.origin],
    request.category,
    request.priority,
    request.status,
    request.owner,
    request.engOwner,
    request.promotedTo,
    ticketSubmitterText(request),
    ticketOriginLabel(request.createdVia),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
}

/**
 * True when every whitespace-separated term appears somewhere in the ticket.
 * Terms are ANDed so "portal tanner" narrows rather than widens.
 */
export function matchesTicketSearch(request: FeatureRequest, search: string): boolean {
  const terms = search.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (terms.length === 0) return true;
  const haystack = searchHaystack(request);
  return terms.every((term) => haystack.includes(term));
}

/** Applies status, type, priority, submitter, assignee, category, then the search box. */
export function filterTickets(
  items: FeatureRequest[],
  query: TicketListQuery,
  context: TicketListContext = EMPTY_CONTEXT,
): FeatureRequest[] {
  return items.filter(
    (request) =>
      matchesTicketBacklog(request, query.backlogOnly, context) &&
      matchesTicketStatuses(request, query.statuses) &&
      matchesTicketTypes(request, query.types) &&
      matchesTicketPriorities(request, query.priorities) &&
      matchesTicketSubmitters(request, query.submitters) &&
      matchesTicketAssignees(request, query.assignees) &&
      matchesTicketCategories(request, query.categories) &&
      matchesTicketSearch(request, query.search),
  );
}

function parseCsvParam(value: string | null): string[] {
  if (!value || value.trim() === '') return [];
  return value
    .split(',')
    .map((part) => {
      const trimmed = part.trim();
      if (!trimmed) return '';
      try {
        return decodeURIComponent(trimmed);
      } catch {
        return trimmed;
      }
    })
    .filter(Boolean);
}

function encodeCsvValues(values: readonly string[]): string {
  return values.map((value) => encodeURIComponent(value)).join(',');
}

function encodeCategoryParam(value: string): string {
  return value === '' ? UNCATEGORIZED_CATEGORY_PARAM : value;
}

function decodeCategoryParam(value: string): string {
  return value === UNCATEGORIZED_CATEGORY_PARAM ? '' : value;
}

/**
 * Resolves a `status` query token into the statuses the list should show.
 * Missing = open (the landing view). `all` = no status filter. `intake` /
 * `open` / a single legacy status still work so shared links don't break.
 */
export function parseStatusParam(value: string | null): FeatureRequestStatus[] | undefined {
  if (value === null) return [...OPEN_STATUSES];
  const tokens = parseCsvParam(value);
  if (tokens.length === 1) {
    const alias = tokens[0];
    if (alias === 'all') return undefined;
    if (alias === 'open') return [...OPEN_STATUSES];
    if (alias === 'intake') return [...INTAKE_STATUSES];
  }
  const statuses = tokens
    .map((token) => (token === 'uat' ? 'acceptance_testing' : token))
    .filter((token): token is FeatureRequestStatus => KNOWN_STATUS.has(token));
  if (statuses.length === 0) return [...OPEN_STATUSES];
  if (statuses.length === FEATURE_REQUEST_STATUSES.length) return undefined;
  return statuses;
}

function parseTypeParam(value: string | null): TicketTypeFilterValue[] | undefined {
  if (value === null || value === 'all') return undefined;
  const types = parseCsvParam(value).filter((token): token is TicketTypeFilterValue =>
    KNOWN_TYPE.has(token),
  );
  return types.length ? types : undefined;
}

function parsePriorityParam(value: string | null): TicketPriorityFilterValue[] | undefined {
  if (value === null || value === 'all') return undefined;
  const priorities = parseCsvParam(value).filter((token): token is TicketPriorityFilterValue =>
    KNOWN_PRIORITY.has(token),
  );
  return priorities.length ? priorities : undefined;
}

function parseCategoryParam(value: string | null): string[] | undefined {
  const categories = parseCsvParam(value).map(decodeCategoryParam);
  return categories.length ? categories : undefined;
}

/** Reads the Tickets list filters from the page URL. */
export function ticketListQueryFromSearchParams(params: URLSearchParams): TicketListQuery {
  const submitters = parseCsvParam(params.get('submitted'));
  const assignees = parseCsvParam(params.get('assigned'));
  return {
    statuses: parseStatusParam(params.get('status')),
    types: parseTypeParam(params.get('type')),
    priorities: parsePriorityParam(params.get('priority')),
    submitters: submitters.length ? submitters : undefined,
    assignees: assignees.length ? assignees : undefined,
    categories: parseCategoryParam(params.get('category')),
    search: params.get('q') ?? '',
    backlogOnly: params.get('view') !== VIEW_ALL_PARAM,
  };
}

/**
 * Writes the Tickets list filters onto `params`. The landing view omits
 * `status` so a bare URL stays the default; "every status" is the explicit
 * `status=all` so it is distinguishable from "I never picked one".
 */
export function writeTicketListQuery(params: URLSearchParams, query: TicketListQuery): void {
  if (
    !query.statuses ||
    query.statuses.length === 0 ||
    sameValues(query.statuses, FEATURE_REQUEST_STATUSES)
  ) {
    params.set('status', 'all');
  } else if (sameValues(query.statuses, OPEN_STATUSES)) {
    params.delete('status');
  } else {
    params.set('status', encodeCsvValues(query.statuses));
  }

  if (!query.types?.length) params.delete('type');
  else params.set('type', encodeCsvValues(query.types));

  if (!query.priorities?.length) params.delete('priority');
  else params.set('priority', encodeCsvValues(query.priorities));

  if (!query.submitters?.length) params.delete('submitted');
  else params.set('submitted', encodeCsvValues(query.submitters));

  if (!query.assignees?.length) params.delete('assigned');
  else params.set('assigned', encodeCsvValues(query.assignees));

  if (!query.categories?.length) params.delete('category');
  else params.set('category', encodeCsvValues(query.categories.map(encodeCategoryParam)));

  if (query.search.trim() === '') params.delete('q');
  else params.set('q', query.search);

  if (query.backlogOnly) params.delete('view');
  else params.set('view', VIEW_ALL_PARAM);
}

/** List route the detail back arrow and breadcrumb return to. */
export const TICKET_LIST_PATH = '/admin/roadmap/requests';

/** sessionStorage key so a refresh on the detail page still knows the last list view. */
export const TICKET_LIST_SEARCH_STORAGE_KEY = 'roadmap.ticketListSearch';

/** Location state stamped when opening a ticket from the filtered list. */
export type TicketListLocationState = {
  ticketListSearch?: string;
};

/** Builds the Tickets list URL, keeping a query string when one is present. */
export function ticketListPath(search?: string | null): string {
  const trimmed = search?.replace(/^\?/, '').trim() ?? '';
  return trimmed ? `${TICKET_LIST_PATH}?${trimmed}` : TICKET_LIST_PATH;
}

/** Reads the list query stamped onto a detail-page location state. */
export function ticketListSearchFromState(state: unknown): string | undefined {
  if (!state || typeof state !== 'object' || !('ticketListSearch' in state)) return undefined;
  const value = (state as TicketListLocationState).ticketListSearch;
  return typeof value === 'string' ? value : undefined;
}

/** Remembers the current Tickets list query for the detail-page back path. */
export function persistTicketListSearch(search: string): void {
  if (typeof sessionStorage === 'undefined') return;
  const trimmed = search.replace(/^\?/, '');
  if (trimmed === '') sessionStorage.removeItem(TICKET_LIST_SEARCH_STORAGE_KEY);
  else sessionStorage.setItem(TICKET_LIST_SEARCH_STORAGE_KEY, trimmed);
}

/** Last Tickets list query, if the current tab had one. */
export function readPersistedTicketListSearch(): string {
  if (typeof sessionStorage === 'undefined') return '';
  return sessionStorage.getItem(TICKET_LIST_SEARCH_STORAGE_KEY) ?? '';
}
